import type { FC } from 'react';
import Button from '@components/Button';
import Modal from '@components/Modal';
import { Show, ShowFirstMatching } from '@components/Show';
import Tooltip from '@components/Tooltip';
import { DialogTitle } from '@components/ui/dialog';
import { type StateFromBlock } from '@ContractBuilder/modules/block-edit';
import { useEntityStore } from '@ContractBuilder/store';
import { isTemplatePath } from '@utils/app-paths';

import { INVALID_CONF_TOOLTIP } from '../constants';
import { BlockVisibilityConfigurationForm } from '../controllers/BlockVisibilityFormController';
import { useVisibilityFormState } from '../hooks/use-visibility-form-state';
import type { SetFormValues } from '../types';

interface VisibilityConfigModalProps {
  blockName?: string;
  formValues: StateFromBlock;
  isDirty?: boolean;
  onCancel: () => void;
  onSaveChanges: () => void | Promise<false | void>;
  setFormValues: SetFormValues;
}

export const BlockVisibilityModalView: FC<VisibilityConfigModalProps> = ({
  blockName,
  formValues,
  isDirty,
  onCancel,
  onSaveChanges,
  setFormValues,
}) => {
  const isTemplate = isTemplatePath();
  const { isLoading } = useEntityStore(({ isLoading }) => ({ isLoading }));
  const isValid = useVisibilityFormState({ formValues });

  const mode = formValues.visibility?.variations?.length ? 'variation' : 'block';
  const tooltipContent = INVALID_CONF_TOOLTIP[mode];

  const buttonDisabled = isDirty ? !isValid : true;

  return (
    <Modal open onClose={onCancel} className={'w-[1000px] sm:max-w-full'}>
      <div className="relative flex w-full min-w-0 flex-col">
        <div className="sm:flex sm:items-start">
          <div className="my-5 text-center sm:mt-0 sm:ml-4 sm:text-left">
            <DialogTitle asChild={true} className="text-info-800 text-lg leading-6 font-medium">
              <h3>
                <ShowFirstMatching>
                  <Show when={!!blockName}>
                    <>Conditional logic for &quot;{blockName}&quot;</>
                  </Show>
                  <Show when={true}>Conditional logic</Show>
                </ShowFirstMatching>
              </h3>
            </DialogTitle>
          </div>
        </div>
        <BlockVisibilityConfigurationForm formValues={formValues} setFormValues={setFormValues} />
        <div className="grow items-center justify-end gap-x-2.5 border-t pt-5 sm:flex">
          <Button className="h-auto px-4 py-2" onClick={onCancel} kind="secondary" size="sm" isDisabled={isLoading}>
            <ShowFirstMatching>
              <Show when={isTemplate}>Cancel</Show>
              <Show when={true}>OK</Show>
            </ShowFirstMatching>
          </Button>
          <Show when={isTemplate}>
            <Tooltip disabled={isValid} content={tooltipContent}>
              <div>
                <Button
                  className="h-auto px-4 py-2"
                  isDisabled={buttonDisabled}
                  kind="primary"
                  loading={isLoading}
                  onClick={onSaveChanges}
                  size="sm"
                >
                  Apply
                </Button>
              </div>
            </Tooltip>
          </Show>
        </div>
      </div>
    </Modal>
  );
};
